import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { spindleApi, dashboardApi, operatorsApi, containersApi, machinesApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import { StepBar, FlowNote, InfoCard, BigButton, ScanZone, QRBox, SectionHeading, Divider, LoadingSpinner, Badge } from '../../../shared/components/UI'
import { clsx } from 'clsx'

const STEPS = ['Machine', 'Position', 'Scan Batch', 'Review', 'Assign', 'Handoff']
const STEP_ICONS = ['🏗️', '📍', '🔍', '📋', '👤', '✅']

export default function SpindlePage() {
  const { user }  = useAuthStore()
  const navigate  = useNavigate()
  const location  = useLocation()
  
  const [step, setStep]             = useState(0)
  const [loading, setLoading]       = useState(true)
  const [machines, setMachines]     = useState([])
  const [positions, setPositions]   = useState([])
  const [operators, setOperators]   = useState([])
  
  const [selectedMachine, setSelectedMachine] = useState(null)
  const [position, setPosition]     = useState(null)
  const [batchInput, setBatchInput] = useState('')
  const [batch, setBatch]           = useState(null)
  const [loadingBatch, setLoadingBatch] = useState(false)
  
  const [removeMode, setRemoveMode] = useState(false)
  const [removeCount, setRemoveCount] = useState('')
  const [newContainerInput, setNewContainerInput] = useState('')
  const [removedContainer, setRemovedContainer] = useState(null)
  
  const [operator, setOperator]     = useState(null)
  const [handoffData, setHandoffData] = useState(null)
  const [error, setError]           = useState('')

  useEffect(() => {
    Promise.all([
      machinesApi.list(),
      dashboardApi.unitStats(),
      operatorsApi.list({ role: 'operator' })
    ]).then(([macRes, statsRes, opsRes]) => {
      // Filter machines by unit if possible, or use all for now as list doesn't take unitId in current api wrapper
      // But we can filter in JS since we have unitName/unitId in user
      const allMachines = macRes.data.data || []
      const unitMachines = allMachines.filter(m => 
        (m.unit_id === user?.unitId || !user?.unitId) && m.type === 'spindle'
      )
      
      setMachines(unitMachines)
      setPositions(statsRes.data.data.positions || [])
      setOperators(opsRes.data.data || [])
    }).finally(() => setLoading(false))
  }, [user])

  const handleScanBatch = () => {
    if (!batchInput.trim()) return
    setLoadingBatch(true)
    setError('')
    spindleApi.scanBatch(batchInput.trim())
      .then(res => {
        setBatch(res.data.data)
        setStep(3)
      })
      .catch(err => setError(err.response?.data?.message || 'Batch not found'))
      .finally(() => setLoadingBatch(false))
  }

  const handleScanRemovedContainer = () => {
    if (!newContainerInput.trim()) return
    setError('')
    containersApi.scan(newContainerInput.trim())
      .then(res => setRemovedContainer(res.data.data))
      .catch(err => setError(err.response?.data?.message || 'Container invalid'))
  }

  const removed   = parseInt(removeCount) || 0
  const remaining = batch ? batch.bobbin_count - removed : 0

  const { mutate, isPending } = useMutation({
    mutationFn: () => spindleApi.handoff({
      batchId: batch.id,
      positionId: position.id,
      operatorId: operator.id,
      bobbinsLoaded: removeMode ? remaining : batch.bobbin_count,
      bobbinsRemoved: removed,
      removedContainerId: removedContainer?.id
    }),
    onSuccess: (res) => {
      setHandoffData(res.data.data)
      setStep(5)
    },
    onError: (err) => setError(err.response?.data?.message || 'Handoff failed')
  })

  if (loading) return <TabletScreen><div className="flex items-center justify-center h-full"><LoadingSpinner size="lg" teal /></div></TabletScreen>

  const filteredPositions = positions.filter(p => p.machine_id === selectedMachine?.id)

  return (
    <TabletScreen>
      <TabletHeader title="Spindle Loading" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">
        {step < 5 && (
          <div className="mb-6 px-1">
             <StepBar steps={STEPS} current={step} />
          </div>
        )}

        {/* Step 0 — Select Machine */}
        {step === 0 && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-4 px-1">
               <SectionHeading>Select Machine</SectionHeading>
               <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Step 1 of 6</span>
            </div>
            
            <div className="grid grid-cols-1 gap-3 mb-6">
              {machines.length === 0 && (
                <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  <p className="text-sm text-gray-400">No active machines found for this unit.</p>
                </div>
              )}
              {machines.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    setSelectedMachine(m)
                    setStep(1)
                  }}
                  className="group flex items-center p-4 rounded-2xl bg-white border border-gray-100 shadow-sm active:scale-[0.98] transition-all text-left hover:border-teal-200 hover:shadow-md"
                >
                  <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-xl mr-4 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
                    🏗️
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-extrabold text-gray-800">{m.name}</p>
                    <p className="text-xs text-gray-400 font-medium">{m.positions?.length || 0} Positions available</p>
                  </div>
                  <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center text-gray-300 group-hover:border-teal-200 group-hover:text-teal-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
            
            <BigButton variant="secondary" onClick={() => navigate('/app/dashboard')}>Cancel & Exit</BigButton>
          </div>
        )}

        {/* Step 1 — Select position */}
        {step === 1 && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-4 px-1">
               <SectionHeading>Machine: {selectedMachine?.name}</SectionHeading>
               <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Step 2 of 6</span>
            </div>
            
            <div className="flex flex-col gap-2 mb-6">
              {filteredPositions.map((p) => {
                const inUse = !!p.current_loading
                const isActive = position?.id === p.id
                return (
                  <button key={p.id}
                    disabled={inUse}
                    onClick={() => setPosition(p)}
                    className={clsx(
                      'relative flex items-center justify-between p-5 rounded-2xl border transition-all text-left',
                      isActive ? 'border-teal-500 bg-teal-50/30 ring-2 ring-teal-500/20' : 'border-gray-100 bg-white hover:border-gray-200',
                      inUse && 'opacity-50 grayscale cursor-not-allowed'
                    )}
                  >
                    <div>
                      <p className={clsx('text-base font-bold', isActive ? 'text-teal-900' : 'text-gray-800')}>{p.name}</p>
                      <p className="text-xs text-gray-400 font-semibold">{p.spindle_count} Spindles</p>
                    </div>
                    {inUse ? (
                      <div className="px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-tighter">In Use</div>
                    ) : isActive ? (
                      <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="px-3 py-1 rounded-full bg-teal-50 text-teal-600 text-[10px] font-black uppercase tracking-tighter">Available</div>
                    )}
                  </button>
                )
              })}
            </div>
            
            <div className="flex flex-col gap-2">
               <BigButton variant="success" disabled={!position} onClick={() => setStep(2)}>
                {position ? `Continue with ${position.name}` : 'Select a position'}
              </BigButton>
              <BigButton variant="secondary" onClick={() => { setStep(0); setPosition(null); setSelectedMachine(null); }}>Back to Machines</BigButton>
            </div>
          </div>
        )}

        {/* Step 2 — Scan batch */}
        {step === 2 && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-4 px-1">
               <SectionHeading>Load Batch</SectionHeading>
               <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Step 3 of 6</span>
            </div>
            
            <FlowNote variant="teal">{selectedMachine?.name} · {position.name} · {position.spindle_count} spindles</FlowNote>
            
            <div className="mb-6">
              <ScanZone teal title="Scan Batch QR" subtitle="Scan the QR code on the batch container" />
              <div className="flex gap-2">
                <input className="form-input flex-1 text-sm h-12" placeholder="Or type batch ID" value={batchInput} onChange={(e) => setBatchInput(e.target.value)} />
                <button onClick={handleScanBatch} disabled={loadingBatch} className="bg-teal-600 text-white px-6 rounded-xl text-sm font-bold shadow-lg shadow-teal-100 active:scale-95 transition-all">
                  {loadingBatch ? '...' : 'Load'}
                </button>
              </div>
              {error && <p className="text-red-500 text-xs mt-3 bg-red-50 p-2 rounded-lg border border-red-100">{error}</p>}
            </div>
            
            <BigButton variant="secondary" onClick={() => { setStep(1); setPosition(null) }}>Back</BigButton>
          </div>
        )}

        {/* Step 3 — Review & decide */}
        {step === 3 && !removeMode && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-4 px-1">
               <SectionHeading>Review & Handoff</SectionHeading>
               <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Step 4 of 6</span>
            </div>

            <FlowNote variant="teal">Batch {batch.batch_number} · {selectedMachine?.name} · {position.name}</FlowNote>
            
            <InfoCard rows={[
              { label: 'Batch #',           value: batch.batch_number,        valueVariant: 'blue' },
              { label: 'Container',         value: batch.container?.container_id },
              { label: 'Shade',             value: batch.shade?.name },
              { label: 'Total bobbins',     value: batch.bobbin_count },
              { label: 'Total weight',      value: `${batch.total_weight_kg} KG` },
              { label: 'Weight per bobbin', value: `${parseFloat(batch.weight_per_bobbin_kg).toFixed(3)} KG` },
            ]} />
            
            <div className="mt-6 mb-2">
               <SectionHeading>How do you want to proceed?</SectionHeading>
            </div>
            
            <div className="flex flex-col gap-3 mb-4">
              <BigButton variant="success" onClick={() => setStep(4)}>Load all {batch.bobbin_count} bobbins</BigButton>
              <BigButton variant="secondary" onClick={() => setRemoveMode(true)}>Remove some bobbins first</BigButton>
            </div>
            <BigButton variant="secondary" onClick={() => setStep(2)}>Re-scan Batch</BigButton>
          </div>
        )}

        {/* Step 3b — Remove bobbins */}
        {step === 3 && removeMode && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-4 px-1">
               <SectionHeading>Remove Bobbins</SectionHeading>
               <span className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Adjustment</span>
            </div>

            <InfoCard rows={[
              { label: 'Batch',             value: batch.batch_number, valueVariant: 'blue' },
              { label: 'Current bobbins',   value: batch.bobbin_count },
              { label: 'Weight per bobbin', value: `${parseFloat(batch.weight_per_bobbin_kg).toFixed(3)} KG` },
            ]} />
            
            <div className="mb-4">
              <label className="form-label text-center mb-2">How many bobbins to remove?</label>
              <input type="number" inputMode="numeric" className="form-input text-4xl text-center py-6 font-black rounded-2xl border-2 focus:border-amber-500 focus:ring-amber-100"
                placeholder="0" min="1" max={batch.bobbin_count - 1} value={removeCount} onChange={(e) => setRemoveCount(e.target.value)} />
            </div>
            
            {removed > 0 && (
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-5 shadow-sm">
                <div className="flex justify-between items-center mb-2">
                   <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Remaining in batch</span>
                   <span className="text-xl font-black text-blue-900">{remaining}</span>
                </div>
                <div className="flex justify-between items-center">
                   <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Weight removed</span>
                   <span className="text-sm font-black text-amber-900">{(removed * parseFloat(batch.weight_per_bobbin_kg)).toFixed(2)} KG</span>
                </div>
              </div>
            )}
            
            <div className="mb-6">
              <SectionHeading>Assign to new container</SectionHeading>
              <ScanZone teal title="Scan New Container" subtitle={`Must be same shade — ${batch.shade?.name}`} />
              <div className="flex gap-2">
                <input className="form-input flex-1 text-sm h-12" placeholder="Or type container ID" value={newContainerInput} onChange={(e) => setNewContainerInput(e.target.value)} />
                <button onClick={handleScanRemovedContainer} className="bg-teal-600 text-white px-6 rounded-xl text-sm font-bold shadow-lg shadow-teal-100 active:scale-95 transition-all">Confirm</button>
              </div>
            </div>

            {removedContainer && <p className="text-xs mb-4 text-center text-teal-700 font-bold bg-teal-50 py-2 rounded-lg">✅ {removedContainer.container_id} verified</p>}
            {error && <p className="text-red-500 text-xs mb-4 bg-red-50 p-2 rounded-lg border border-red-100">{error}</p>}
            
            <div className="flex flex-col gap-2">
              <BigButton variant="success" disabled={!removedContainer || removed < 1} onClick={() => setStep(4)}>Confirm & Proceed</BigButton>
              <BigButton variant="secondary" onClick={() => setRemoveMode(false)}>Cancel Adjustment</BigButton>
            </div>
          </div>
        )}

        {/* Step 4 — Assign operator */}
        {step === 4 && (
          <div className="animate-slide-up">
            <div className="flex items-center justify-between mb-4 px-1">
               <SectionHeading>Assign Operator</SectionHeading>
               <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Step 5 of 6</span>
            </div>

            <InfoCard rows={[
              { label: 'Machine/Pos', value: `${selectedMachine?.name} · ${position.name}`, valueVariant: 'blue' },
              { label: 'Batch #',    value: batch.batch_number,                               valueVariant: 'blue' },
              { label: 'Bobbins',  value: removeMode ? remaining : batch.bobbin_count },
              { label: 'Shade',    value: batch.shade?.name },
            ]} />
            
            <div className="mt-6 mb-4">
               <SectionHeading>Select operator</SectionHeading>
            </div>
            
            <div className="flex flex-col gap-3 mb-6">
              {operators.map((op) => (
                <button 
                  key={op.id} 
                  onClick={() => setOperator(op)} 
                  className={clsx(
                    'flex items-center gap-4 p-4 rounded-2xl border transition-all text-left active:scale-[0.98]',
                    operator?.id === op.id ? 'border-teal-500 bg-teal-50/30 ring-2 ring-teal-500/20' : 'border-gray-100 bg-white hover:border-gray-200'
                  )}
                >
                  <div className={clsx(
                    'w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black flex-shrink-0 shadow-sm',
                    operator?.id === op.id ? 'bg-teal-500 text-white' : 'bg-brand-50 text-brand-700'
                  )}>
                    {op.name.substring(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-extrabold text-gray-800">{op.name}</p>
                    <p className="text-xs text-gray-400 font-medium">Operator · {op.phone || '--'}</p>
                  </div>
                  {operator?.id === op.id && (
                    <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
            
            {error && <p className="text-red-500 text-xs mb-4 bg-red-50 p-2 rounded-lg border border-red-100">{error}</p>}
            
            <div className="flex flex-col gap-2">
              <BigButton variant="success" disabled={!operator || isPending} onClick={() => mutate()}>
                {isPending ? 'Generating…' : 'Generate Handoff QR'}
              </BigButton>
              <BigButton variant="secondary" onClick={() => setStep(3)}>Back</BigButton>
            </div>
          </div>
        )}

        {/* Step 5 — Handoff QR */}
        {step === 5 && (
          <div className="animate-slide-up">
            <div className="text-center mb-6 mt-4">
              <div className="w-16 h-16 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xl font-black text-gray-800 mb-1">Issue QR to Operator</p>
              <p className="text-sm text-gray-400 font-medium px-8 leading-relaxed">
                Operator will scan this QR code when they return the empty bobbins.
              </p>
            </div>
            
            <div className="bg-white rounded-3xl border border-gray-100 p-8 shadow-xl mb-6 flex flex-col items-center">
              <QRBox label={handoffData?.handoff_qr_id} sublabel={`Issued: ${new Date().toLocaleTimeString()}`} size={160} />
              <div className="mt-6 text-center">
                <p className="text-sm font-black text-gray-800">{handoffData?.handoff_qr_id}</p>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Handoff Reference</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 mb-6">
               <InfoCard rows={[
                { label: 'Machine/Pos',       value: `${selectedMachine?.name} · ${position.name}`, valueVariant: 'blue' },
                { label: 'Batch #',          value: batch.batch_number,  valueVariant: 'blue' },
                { label: 'Operator',       value: operator?.name },
                { label: 'Bobbins',        value: removeMode ? remaining : batch.bobbin_count },
                { label: 'Shade',          value: batch.shade?.name },
              ]} />
            </div>
            
            <BigButton variant="primary" onClick={() => navigate('/app/dashboard')}>Complete & Return Home</BigButton>
          </div>
        )}
      </div>
    </TabletScreen>
  )
}
